/**
 * AI Scribe — raw PCM microphone capture
 *
 * Drop-in alternative to `useMediaRecorder` for sockets that expect a raw sample
 * stream instead of a container. `useMediaRecorder` emits `audio/webm` (Opus)
 * blobs; this hook emits **16-bit LE PCM, mono, 16 kHz** `ArrayBuffer` frames,
 * produced by an `AudioWorklet` that downsamples the device rate itself.
 *
 * @author [Rajkumar Rathod]
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  AUDIO_CONTEXT_STATE,
  PCM_AUDIO_CONSTRAINTS,
  PCM_FRAME_SIZE,
  PCM_PROCESSOR_NAME,
  PCM_TARGET_SAMPLE_RATE,
  PCM_WORKLET_MODULE_URL,
} from "./pcm-audio-constant";

interface UsePcmAudioStreamProps {
  /** Called for every PCM frame; wire it straight to the socket's `send`. */
  onFrame?: (frame: ArrayBuffer) => void;
}

interface UsePcmAudioStreamReturn {
  isRecording: boolean;
  isPaused: boolean;
  error: Error | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
}

export function usePcmAudioStream({
  onFrame,
}: UsePcmAudioStreamProps): UsePcmAudioStreamReturn {
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  /** Gate read inside the worklet callback — a paused session keeps the graph alive but sends nothing. */
  const isStreamingRef = useRef<boolean>(false);
  /** Held in a ref so re-created callbacks never rebuild the audio graph. */
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /** Tears the whole graph down: worklet, context, and every mic track. */
  const teardown = useCallback(() => {
    isStreamingRef.current = false;

    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    sourceNodeRef.current?.disconnect();
    sourceNodeRef.current = null;

    audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    /** A second start on a live graph would leak the first one. */
    if (audioContextRef.current) teardown();

    try {
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: PCM_AUDIO_CONSTRAINTS,
      });
      streamRef.current = stream;

      /**
       * Native rate — asking the context for 16 kHz makes Firefox and Safari
       * resample badly. The worklet downsamples instead.
       */
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      await audioContext.audioWorklet.addModule(PCM_WORKLET_MODULE_URL);

      const source = audioContext.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      const workletNode = new AudioWorkletNode(
        audioContext,
        PCM_PROCESSOR_NAME,
        {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          channelCount: 1,
          processorOptions: {
            targetSampleRate: PCM_TARGET_SAMPLE_RATE,
            frameSize: PCM_FRAME_SIZE,
          },
        }
      );
      workletNodeRef.current = workletNode;

      workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (!isStreamingRef.current) return;
        onFrameRef.current?.(event.data);
      };

      source.connect(workletNode);

      /**
       * The node still has to reach the destination for the graph to be pulled,
       * but at zero gain — routing the live mic to the speakers would feed back.
       */
      const silentSink = audioContext.createGain();
      silentSink.gain.value = 0;
      workletNode.connect(silentSink);
      silentSink.connect(audioContext.destination);

      isStreamingRef.current = true;
      setIsRecording(true);
      setIsPaused(false);
    } catch (err) {
      teardown();
      const failure =
        err instanceof Error ? err : new Error("Microphone access failed");
      setError(failure);
      setIsRecording(false);
    }
  }, [teardown]);

  const stopRecording = useCallback(() => {
    teardown();
    setIsRecording(false);
    setIsPaused(false);
  }, [teardown]);

  /**
   * The graph is deliberately left running: suspending an `AudioContext` that
   * owns a `MediaStreamAudioSourceNode` leaves the source detached from the
   * track on resume in Chromium, so the worklet would only ever see silence
   * again. Pausing mutes the track and closes the `isStreamingRef` gate
   * instead — nothing is sent, and resuming is a pure toggle.
   */
  const setTracksEnabled = useCallback((enabled: boolean) => {
    streamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }, []);

  const pauseRecording = useCallback(() => {
    isStreamingRef.current = false;
    setTracksEnabled(false);
    setIsPaused(true);
  }, [setTracksEnabled]);

  const resumeRecording = useCallback(() => {
    /** Only ever needed when the browser suspended the context on its own. */
    if (audioContextRef.current?.state === AUDIO_CONTEXT_STATE.SUSPENDED)
      void audioContextRef.current.resume().catch(() => undefined);

    setTracksEnabled(true);
    isStreamingRef.current = true;
    setIsPaused(false);
  }, [setTracksEnabled]);

  /** Unmount must release the microphone and the audio context. */
  const teardownRef = useRef(teardown);
  teardownRef.current = teardown;
  useEffect(() => () => teardownRef.current(), []);

  return {
    isRecording,
    isPaused,
    error,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
  };
}
